import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import SectionHeader from '../components/SectionHeader';
import WorkAreaCard from '../components/WorkAreaCard';
import GoalCard from '../components/GoalCard';
import '../styles/HomePage.css';
import SEO from '../components/SEO';
import { API_BASE_URL } from '../config';
import {
  TAGLINE,
  HEADLINE,
  INTRO_PARAGRAPHS,
  VISION,
  MISSION,
  GOALS,
  WORK_AREAS,
  APPROACH,
  WHY_EF21,
  IMPACT_STATS,
  PUBLICATIONS,
} from '../data/content';

interface ImageData {
  url: string;
  id: string;
}

interface ContentBlock {
  type: 'paragraph' | 'images';
  content: string;
  order: number;
}

interface Activity {
  _id: string;
  title: string;
  date: string;
  location: string;
  content: ContentBlock[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

const HomePage: React.FC = () => {
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState<boolean>(true);

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    try {
      setLoadingActivities(true);
      const response = await fetch(`${API_BASE_URL}/api/activities`);

      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }

      const data = await response.json();
      // Filter only published activities and get the 3 most recent
      const publishedActivities = data
        .filter((activity: Activity) => activity.status === 'published')
        .slice(0, 3);

      setRecentActivities(publishedActivities);
    } catch (err) {
      console.error('Failed to load recent activities:', err);
    } finally {
      setLoadingActivities(false);
    }
  };

  // Get first paragraph and first image for preview
  const getActivityPreview = (activity: Activity) => {
    let previewText = '';
    let previewImage = '';

    // Find first paragraph
    const firstParagraph = activity.content.find(block =>
      block.type === 'paragraph' && block.content.trim() !== ''
    );

    if (firstParagraph) {
      previewText = firstParagraph.content;
      // Limit preview text to first 150 characters
      if (previewText.length > 150) {
        previewText = previewText.substring(0, 150) + '...';
      }
    }

    // Find first image
    const firstImageBlock = activity.content.find(block =>
      block.type === 'images' && block.content && block.content !== '[]'
    );

    if (firstImageBlock) {
      try {
        const images = JSON.parse(firstImageBlock.content) as ImageData[];
        if (images.length > 0) {
          previewImage = images[0].url;
        }
      } catch (e) {
        console.error('Error parsing image data', e);
      }
    }

    return { previewText, previewImage };
  };

  // Format date in a readable way
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <SEO
        title="Home"
        description="Empower Future 21 (EF21) is a women-led Myanmar humanitarian and development organization strengthening education and community resilience in crisis-affected areas."
        keywords="EF21, Empower Future 21, Myanmar, education in emergencies, humanitarian, women-led organization, community resilience, teacher training, MHPSS, locally led"
      />
      <Hero
        eyebrow={TAGLINE}
        title={HEADLINE}
        subtitle={INTRO_PARAGRAPHS[0]}
        primaryButtonText="Explore Our Work"
        primaryButtonLink="/what-we-do"
        secondaryButtonText="Partner With Us"
        secondaryButtonLink="/contact"
      />

      <section id="intro" className="intro">
        <div className="container">
          {INTRO_PARAGRAPHS.slice(1).map((paragraph, index) => (
            <p className="intro-paragraph" key={index}>{paragraph}</p>
          ))}
        </div>
      </section>

      <section id="mission" className="mission">
        <div className="container">
          <div className="mission-vision-grid">
            <div className="mission-card">
              <div className="card-icon">🎯</div>
              <h3>Our Mission</h3>
              <p>{MISSION}</p>
            </div>
            <div className="vision-card">
              <div className="card-icon">✨</div>
              <h3>Our Vision</h3>
              <p>{VISION}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="impact" className="impact">
        <div className="container">
          <SectionHeader
            title="Our Impact"
            subtitle="Reaching children, teachers, and communities across conflict-affected Myanmar"
          />

          <div className="impact-grid">
            {IMPACT_STATS.map((stat, index) => (
              <div className="impact-card" key={index}>
                <div className="impact-card-icon" aria-hidden="true">{stat.icon}</div>
                <div className="impact-value">{stat.value}</div>
                <p className="impact-description">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="what-we-do" className="work-areas">
        <div className="container">
          <SectionHeader
            title="What We Do"
            subtitle="Our areas of work respond to locally identified priorities in crisis-affected communities"
          />

          <div className="work-areas-grid">
            {WORK_AREAS.map(area => (
              <WorkAreaCard
                key={area.id}
                icon={area.icon}
                title={area.title}
                description={area.description}
                items={area.items}
              />
            ))}
          </div>

          <div className="section-cta">
            <Link to="/what-we-do" className="btn">Explore Our Work</Link>
          </div>
        </div>
      </section>

      <section id="recent-activities" className="recent-activities">
        <div className="container">
          <SectionHeader
            title="Recent Activities"
            subtitle="See our latest work in action and the impact we're making in communities"
          />

          {loadingActivities ? (
            <div className="activities-loading">
              <div className="loading-spinner"></div>
              <p>Loading activities...</p>
            </div>
          ) : recentActivities.length > 0 ? (
            <>
              <div className="activities-grid">
                {recentActivities.map(activity => {
                  const { previewText, previewImage } = getActivityPreview(activity);
                  return (
                    <div className="activity-card" key={activity._id}>
                      {previewImage && (
                        <div className="activity-image">
                          <img src={previewImage} alt={activity.title} />
                        </div>
                      )}
                      <div className="activity-content">
                        <div className="activity-meta">
                          <span className="activity-date">{formatDate(activity.date)}</span>
                          <span className="activity-location">{activity.location}</span>
                        </div>
                        <h3 className="activity-title">{activity.title}</h3>
                        {previewText && <p className="activity-preview">{previewText}</p>}
                        <Link to={`/activities/${activity._id}`} className="activity-link">
                          Read More →
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="view-all-activities">
                <Link to="/activities" className="btn">View All Activities</Link>
              </div>
            </>
          ) : (
            <div className="no-activities">
              <p>No activities published yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      <section id="approach" className="approach">
        <div className="container">
          <SectionHeader
            title="How We Work"
            subtitle="Our approach to locally led, evidence-informed humanitarian and development programming"
          />

          <div className="approach-grid">
            {APPROACH.map((item, index) => (
              <div className="approach-card" key={index}>
                <div className="approach-icon" aria-hidden="true">{item.icon}</div>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="goals" className="goals">
        <div className="container">
          <SectionHeader
            title="Our Goals"
            subtitle="Five goals guide our programmes and our growth as a national organization"
          />

          {GOALS.map(goal => (
            <GoalCard
              key={goal.number}
              number={goal.number}
              title={goal.title}
              description={goal.description}
            />
          ))}
        </div>
      </section>

      <section id="research" className="research">
        <div className="container">
          <SectionHeader
            title="Latest Research & Learning"
            subtitle="Evidence and learning that inform humanitarian decision-making and programme quality"
          />

          <div className="research-grid">
            {PUBLICATIONS.map((publication, index) => (
              <div className="research-card" key={index}>
                <span className="research-type">{publication.type}</span>
                <h4>{publication.title}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="why-ef21" className="why-ef21">
        <div className="container">
          <SectionHeader
            title="Why EF21?"
            subtitle="What we bring to partnerships and to the communities we serve"
          />

          <div className="why-grid">
            {WHY_EF21.map((reason, index) => (
              <div className="why-card" key={index}>
                <span className="why-icon" aria-hidden="true">{reason.icon}</span>
                <h4>{reason.title}</h4>
                <p>{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="contact">
        <div className="container">
          <SectionHeader
            title="Get Involved"
            subtitle="Work with us to strengthen education and resilience in crisis-affected Myanmar."
          />
          <div className="get-involved-btns">
            <Link to="/contact" className="btn">Partner with EF21</Link>
            <Link to="/donate" className="btn">Support Our Work</Link>
            <Link to="/careers" className="btn">Join Our Team</Link>
            <Link to="/contact" className="btn btn-outline">Contact Us</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
